import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { formatTourId } from '../utils/tour';

const STATUS_BADGE = {
  draft: <span className="badge badge-draft">Draft</span>,
  submitted: <span className="badge badge-submitted">Pending Review</span>,
  acknowledged: <span className="badge badge-acknowledged">In Progress</span>,
  resolved: <span className="badge badge-resolved">Resolved</span>,
  escalated: <span className="badge badge-escalated">⚠ Escalated</span>,
};

const TOUR_ROLES = ['staff', 'building_admin', 'regional_manager', 'executive_staff', 'super_admin'];

export default function TourList() {
  const { user, API } = useAuth();
  const [searchParams] = useSearchParams();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [facilityFilter, setFacilityFilter] = useState('');
  const [conductorFilter, setConductorFilter] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [facilities, setFacilities] = useState([]);
  const [conductors, setConductors] = useState([]);
  const [page, setPage] = useState(1);

  // Dropdown data — loaded once. Staff see only themselves in conductors;
  // building admins won't need the facility filter but it's harmless.
  useEffect(() => {
    API.get('/admin/facilities').then(r => setFacilities(r.data)).catch(() => {});
    API.get('/tours/conductors').then(r => setConductors(r.data)).catch(() => {});
  }, []);

  const loadTours = () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 25 });
    if (statusFilter) params.set('status', statusFilter);
    if (facilityFilter) params.set('facilityId', facilityFilter);
    if (conductorFilter) params.set('conductedBy', conductorFilter);
    if (sortBy) params.set('sort', sortBy);
    API.get(`/tours?${params}`).then(r => setTours(r.data.tours)).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTours();
  }, [page, statusFilter, facilityFilter, conductorFilter, sortBy]);

  // Any filter change goes back to page 1
  useEffect(() => { setPage(1); }, [statusFilter, facilityFilter, conductorFilter, sortBy]);

  const canStartTour = TOUR_ROLES.includes(user?.role);

  // Backend permits delete for super_admin/RM/exec on any tour, plus the creator on their own draft.
  const elevatedRoles = ['super_admin', 'regional_manager', 'executive_staff'];
  const canDeleteTour = (t) =>
    elevatedRoles.includes(user?.role) ||
    (t.status === 'draft' && t.conducted_by === user?.id);

  const handleDelete = async (t) => {
    const label = t.status === 'draft'
      ? 'Delete this draft? This cannot be undone.'
      : 'Permanently delete this tour? This cannot be undone.';
    if (!window.confirm(label)) return;
    try {
      await API.delete(`/tours/${t.id}`);
      loadTours();
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.error || e.message));
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h2>All Tours</h2>
          <p>View and manage facility inspection records</p>
        </div>
        {canStartTour && (
          <Link to="/tours/new" className="btn btn-primary">+ Start New Tour</Link>
        )}
      </div>

      <div className="page-body">
        <div className="card">
          {/* Filters */}
          <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap',alignItems:'center'}}>
            <select className="form-control" style={{width:'auto'}} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="submitted">Pending Review</option>
              <option value="acknowledged">In Progress</option>
              <option value="escalated">Escalated</option>
              <option value="resolved">Resolved</option>
              <option value="draft">Draft</option>
            </select>
            <select className="form-control" style={{width:'auto'}} value={facilityFilter} onChange={e => setFacilityFilter(e.target.value)}>
              <option value="">All Facilities</option>
              {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <select className="form-control" style={{width:'auto'}} value={conductorFilter} onChange={e => setConductorFilter(e.target.value)}>
              <option value="">All Conductors</option>
              {conductors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select className="form-control" style={{width:'auto'}} value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="date_desc">Newest first</option>
              <option value="date_asc">Oldest first</option>
              <option value="facility">Facility A–Z</option>
              <option value="conducted_by">Conducted by A–Z</option>
              <option value="status">By status</option>
            </select>
            {(statusFilter || facilityFilter || conductorFilter || sortBy !== 'date_desc') && (
              <button className="btn btn-secondary btn-sm" onClick={() => { setStatusFilter(''); setFacilityFilter(''); setConductorFilter(''); setSortBy('date_desc'); }}>
                Clear
              </button>
            )}
          </div>

          {loading ? (
            <div style={{textAlign:'center',padding:32}}><div className="spinner" style={{margin:'0 auto'}}/></div>
          ) : tours.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No tours found</h3>
              <p>Try adjusting your filters or start a new tour.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tour ID</th>
                    <th>Facility</th>
                    <th>Conducted By</th>
                    <th>Date</th>
                    <th>Score</th>
                    <th>Open Issues</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tours.map(t => {
                    const score = parseFloat(t.compliance_score);
                    const scoreColor = score >= 90 ? 'var(--green)' : score >= 75 ? 'var(--amber)' : 'var(--red)';
                    return (
                      <tr key={t.id}>
                        <td style={{fontFamily:'DM Mono, monospace',fontSize:13,color:'var(--gray-700)'}}>
                          {formatTourId(t.tour_number)}
                        </td>
                        <td style={{fontWeight:600}}>{t.facility_name}</td>
                        <td>{t.staff_name}</td>
                        <td style={{color:'var(--gray-500)',fontSize:13}}>
                          {t.submitted_at
                            ? format(new Date(t.submitted_at), 'MMM d, yyyy')
                            : (
                              <span>
                                Started {format(new Date(t.created_at), 'MMM d, yyyy')}
                                {(Date.now() - new Date(t.created_at).getTime()) > 86400000 && (
                                  <span style={{ color: 'var(--amber)', fontWeight: 700, marginLeft: 6 }} title="This draft is over a day old — tours should be completed same-day. It blocks new tours at this facility until submitted, deleted, or 7 days pass.">
                                    ⚠ stale
                                  </span>
                                )}
                              </span>
                            )}
                        </td>
                        <td>
                          {t.compliance_score
                            ? <span style={{fontWeight:700,color:scoreColor}}>{score.toFixed(1)}%</span>
                            : '—'}
                        </td>
                        <td>
                          {parseInt(t.open_issues) > 0
                            ? <span style={{color:'var(--red)',fontWeight:700}}>{t.open_issues} open</span>
                            : <span style={{color:'var(--green)'}}>✓ Clear</span>}
                        </td>
                        <td>{STATUS_BADGE[t.status]}</td>
                        <td>
                          <div style={{display:'flex',gap:6,justifyContent:'flex-end',flexWrap:'wrap'}}>
                            {t.status === 'draft' ? (
                              <Link to={`/tours/${t.id}/edit`} className="btn btn-primary btn-sm">Resume</Link>
                            ) : (
                              <Link to={`/tours/${t.id}`} className="btn btn-secondary btn-sm">View</Link>
                            )}
                            {canDeleteTour(t) && (
                              <button
                                className="btn btn-sm"
                                style={{background:'#FCEBEB',color:'#A32D2D',border:'1px solid #F09595'}}
                                onClick={() => handleDelete(t)}
                                title="Delete this tour"
                              >🗑</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:16}}>
            <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(p => p-1)}>← Prev</button>
            <span style={{padding:'6px 12px',fontSize:13,color:'var(--gray-500)'}}>Page {page}</span>
            <button className="btn btn-secondary btn-sm" disabled={tours.length < 25} onClick={() => setPage(p => p+1)}>Next →</button>
          </div>
        </div>
      </div>
    </>
  );
}
